// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.
import { createSelector } from 'reselect'
import { createAggressiveDeepEqualSelector } from './createDeepEqualSelector'

import { fullFileStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Books} Books
 * @typedef {import('../index').Book} Book
 */

/**
 * @type {function(PlottrState): Books}
 */
export const allBooksSelector = createSelector(fullFileStateSelector, ({ books }) => books ?? {})

/**
 * @type {function(PlottrState): Array<number>}
 */
export const allBookIdsSelector = createSelector(allBooksSelector, ({ allIds }) => {
  if (Array.isArray(allIds)) {
    return allIds
  } else {
    return []
  }
})

/**
 * @type {function(PlottrState): Array<Book>}
 */
export const allBooksAsArraySelector = createAggressiveDeepEqualSelector(
  allBooksSelector,
  (books) => {
    /**
     * @param {Array<Book>} acc
     * @param {Book | Array<number>} bookOrIds
     * @return {Array<Book>}
     */
    const accumulate = (acc, bookOrIds) => {
      if (!Array.isArray(bookOrIds)) {
        return [...acc, bookOrIds]
      } else {
        return acc
      }
    }
    return Object.values(books).reduce(accumulate, [])
  }
)

/**
 * @param {PlottrState} _state
 * @param {'series' | number} bookId
 * @return {'series' | number}
 */
const bookIdSelector = (_state, bookId) => {
  return bookId
}
/**
 * @type {function(PlottrState, 'series' | number): (Book | Null)}
 */
export const bookByIdSelector = createSelector(
  allBooksSelector,
  bookIdSelector,
  (books, bookId) => {
    const book = books[bookId]
    if (book && !Array.isArray(book)) {
      return book
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState, number | 'series'): Book | Null}
 */
export const singleBookSelector = createSelector(
  allBooksSelector,
  bookIdSelector,
  (books, bookId) => {
    const book = books[bookId]
    if (book && !Array.isArray(book)) {
      return book
    } else {
      return null
    }
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const canDeleteBookSelector = createSelector(
  allBookIdsSelector,
  (bookIds) => bookIds.length > 1
)
